import axios from 'axios';
import { message } from 'antd';
import { getTokens, saveTokens, clearTokens } from './authApi';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/admin/reports';

const reportsApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

// Add authentication token to all requests
reportsApi.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh and 401 errors
reportsApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`,
            { refreshToken: tokens.refreshToken },
          );
          const { tokens: newTokens } = response.data;
          saveTokens(newTokens);
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return reportsApi(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ── Types (mirroring ReportsService OperationalReport) ───────────────────────

export type ReportRange = 'today' | 'week' | 'mtd' | 'ytd';

export interface DateRange {
  from: string;
  to: string;
}

export interface WoStatusCount {
  status: string;
  count: number;
}

export interface TechProductivity {
  technician: string;
  completedCount: number;
}

export interface AppointmentVolumeDay {
  date: string;
  scheduled: number;
  completed: number;
  cancelled: number;
  no_show: number;
  other: number;
}

export interface DviCompletionRateByTech {
  technicianId: number;
  total: number;
  completed: number;
  rate: number | null;
}

export interface AppointmentUtilizationDay {
  date: string;
  technicianId: number | null;
  totalMinutes: number;
}

export interface OperationalReport {
  range: DateRange;
  woByStatus: WoStatusCount[];
  techProductivity: TechProductivity[];
  appointmentVolumeByDay: AppointmentVolumeDay[];
  estimateConversionRate: number | null;
  estimateConversionNumerator: number;
  estimateConversionDenominator: number;
  dviCompletionRateOverall: number | null;
  dviTotal: number;
  dviCompleted: number;
  dviByTech: DviCompletionRateByTech[];
  avgCycleTimeHours: number | null;
  jobsInProgress: number;
  noShowRate: number | null;
  noShowCount: number;
  totalAppointments: number;
  appointmentUtilizationByDay: AppointmentUtilizationDay[];
  partsEtaAdherenceRate: number | null;
  partsEtaNumerator: number;
  partsEtaDenominator: number;
}

// ── API functions ─────────────────────────────────────────────────────────────

export const getOperationalReport = async (range: ReportRange): Promise<OperationalReport> => {
  const response = await reportsApi.get<OperationalReport>('/operational', {
    params: { range },
  });
  return response.data;
};

/**
 * Triggers a CSV download via the `reportsApi` axios instance so the shared
 * 401-refresh interceptor applies (expired token → refresh + retry instead of a
 * dead 401). Blob responseType avoids embedding credentials in the URL.
 *
 * G-C NIT-b: the returned Promise is surfaced so callers can reset loading
 * state reliably. A `.catch()` handler surfaces `message.error` and resolves
 * the loading state without relying solely on the setTimeout fallback.
 *
 * @param range     The report date-range preset.
 * @param onFinally Optional callback invoked on both success and error so the
 *                  caller can reset its own loading state immediately (instead
 *                  of depending only on the 1.5 s setTimeout in ReportsPage).
 */
export const downloadOperationalCsv = (
  range: ReportRange,
  onFinally?: () => void,
): Promise<void> => {
  return reportsApi
    .get('/operational.csv', { params: { range }, responseType: 'blob' })
    .then((response) => {
      const blob = response.data as Blob;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `cnf-operational-report-${range}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    })
    .catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : 'CSV export failed — please try again.';
      message.error(msg);
    })
    .finally(() => {
      onFinally?.();
    });
};
